import { PostgrestResponse } from '@supabase/supabase-js';
import { IVwRelatorioConsumoAluno } from '../../../types/views/IVwRelatorioConsumoAluno';
import { BaseMultiTenantService } from '../base/BaseMultiTenantService';

/**
 * View para relatório de consumo por aluno
 * Corresponde à view 'vw_relatorio_consumo_aluno' no banco de dados
 */
export class ViewRelatorioConsumoAluno extends BaseMultiTenantService<IVwRelatorioConsumoAluno> {
     constructor() {
          super('vw_relatorio_consumo_aluno', [], 'organizacao');
     }

     /**
      * Relatório completo de consumo por aluno
      */
     async getRelatorioConsumo(organizationId: number): Promise<PostgrestResponse<IVwRelatorioConsumoAluno[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .order('valor_total_gasto', { ascending: false });
     }

     /**
      * Consumo de um aluno específico
      */
     async getConsumoPorAluno(organizationId: number, alunoId: number): Promise<PostgrestResponse<IVwRelatorioConsumoAluno>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('id_aluno', alunoId)
               .single();
     }

     /**
      * Top consumidores
      */
     async getTopConsumidores(organizationId: number, limite: number = 10): Promise<PostgrestResponse<IVwRelatorioConsumoAluno[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .order('valor_total_gasto', { ascending: false })
               .limit(limite);
     }

     /**
      * Alunos com gasto acima da média
      */
     async getAlunosGastoAcimaMedia(organizationId: number): Promise<PostgrestResponse<IVwRelatorioConsumoAluno[]>> {
          // Primeiro busca a média
          const { data: media } = await this.client
               .from(this.tableName)
               .select('valor_total_gasto.avg()')
               .eq(this.organizationIdField, organizationId);

          const valorMedio = media?.[0]?.avg || 0;

          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .gt('valor_total_gasto', valorMedio)
               .order('valor_total_gasto', { ascending: false });
     }

     /**
      * Consumo por responsável (usando view que já agrupa)
      */
     async getConsumoPorResponsavel(organizationId: number): Promise<any> {
          return await this.client
               .from(this.tableName)
               .select('responsavel, valor_total_gasto')
               .eq(this.organizationIdField, organizationId)
               .order('valor_total_gasto', { ascending: false });
     }

     /**
      * Estatísticas gerais de consumo
      */
     async getEstatisticasConsumo(organizationId: number): Promise<{
          sum: number;
          avg: number;
          max: number;
          min: number;
          total_compras: number;
     }> {
          const { data } = await this.client
               .from(this.tableName)
               .select(`
                    valor_total_gasto.sum(),
                    valor_total_gasto.avg(),
                    valor_total_gasto.max(),
                    valor_total_gasto.min(),
                    total_compras.sum()
               `)
               .eq(this.organizationIdField, organizationId);

          return {
               sum: data?.[0]?.sum || 0,
               avg: data?.[0]?.avg || 0,
               max: data?.[0]?.max || 0,
               min: data?.[0]?.min || 0,
               total_compras: (data?.[0] as any)?.sum_1 || 0
          };
     }
}

export const viewRelatorioConsumoAluno = new ViewRelatorioConsumoAluno();
